import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { KeyRound, Save, UserPlus, Users, Power, ShieldCheck, Stethoscope, Headset, Camera, User } from 'lucide-react';

export default function ChangePasswordPage() {
  return (
    <AppLayout title="Account Settings" subtitle="Manage your profile, password and clinic staff accounts">
      <div className="account-settings-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 24, alignItems: 'start' }}>
        {/* Left column — Profile */}
        <div>
          <ProfileCard />
        </div>
        {/* Right column — Username + Password + Staff stacked */}
        <div style={{ display: 'grid', gap: 24 }}>
          <ChangeUsernameCard />
          <ChangePasswordCard />
          <StaffManagementCard />
        </div>
      </div>
    </AppLayout>
  );
}

// ── Profile Card ─────────────────────────────────────────────────────────────
function ProfileCard() {
  const { user: authUser } = useAuth();
  const { showToast } = useToast();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ displayName:'', phone:'', email:'', address:'', specialization:'', profilePicture:'' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      const u = r.data.user;
      setForm({
        displayName:    u.displayName    || '',
        phone:          u.phone          || '',
        email:          u.email          || '',
        address:        u.address        || '',
        specialization: u.specialization || '',
        profilePicture: u.profilePicture || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  function handlePicture(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { showToast('Image must be under 2MB', 'error'); return; }
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, profilePicture: ev.target.result }));
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.put('/auth/profile', form);
      showToast('Profile updated successfully.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update profile.', 'error');
    } finally { setSaving(false); }
  }

  if (loading) return null;

  return (
    <div className="card" style={{ padding: 24 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20 }}>
        <User size={18} color="var(--color-primary)" />
        <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>My Profile</h3>
        <span style={{ fontSize:12, color:'var(--color-ink-faint)', marginLeft:4 }}>
          ({authUser?.role === 'doctor' ? 'Doctor' : 'Receptionist'} · {authUser?.username})
        </span>
      </div>

      <div style={{ display:'flex', gap:24, alignItems:'flex-start', flexWrap:'wrap' }}>
        {/* Profile picture */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, flexShrink:0 }}>
          <div style={{ width:90, height:90, borderRadius:'50%', overflow:'hidden', background:'var(--color-primary-soft)', border:'3px solid var(--color-primary)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', position:'relative' }}
            onClick={() => fileRef.current?.click()}>
            {form.profilePicture
              ? <img src={form.profilePicture} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              : <div style={{ fontSize:32, fontWeight:800, color:'var(--color-primary)' }}>
                  {(form.displayName || authUser?.username || '?').charAt(0).toUpperCase()}
                </div>
            }
            <div style={{ position:'absolute', bottom:0, right:0, background:'var(--color-primary)', borderRadius:'50%', width:26, height:26, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Camera size={13} color="white" />
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handlePicture} />
          <div style={{ fontSize:10, color:'var(--color-ink-faint)', textAlign:'center' }}>Click to change<br/>Max 2MB</div>
        </div>

        {/* Fields */}
        <div style={{ flex:1, minWidth:240, display:'grid', gap:12 }}>
          <div className="field">
            <label className="label">Full Name / Display Name</label>
            <input className="input" value={form.displayName} onChange={e => setForm(f=>({...f,displayName:e.target.value}))}
              placeholder="e.g. Dr. Vijaya Kumar" />
          </div>
          {authUser?.role === 'doctor' && (
            <div className="field">
              <label className="label">Qualification / Specialization</label>
              <input className="input" value={form.specialization} onChange={e => setForm(f=>({...f,specialization:e.target.value}))}
                placeholder="e.g. BDS, MDS Orthodontics" />
            </div>
          )}
          <div className="field">
            <label className="label">Phone Number</label>
            <input className="input" value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))}
              placeholder="e.g. 9876543210" />
          </div>
          <div className="field">
            <label className="label">Email Address</label>
            <input className="input" type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))}
              placeholder="e.g. doctor@vijayasdentalcare.com" />
          </div>
          <div className="field">
            <label className="label">Address</label>
            <input className="input" value={form.address} onChange={e => setForm(f=>({...f,address:e.target.value}))}
              placeholder="e.g. 12 Main Street, Chidambaram" />
          </div>
        </div>
      </div>

      <div style={{ marginTop:16, display:'flex', justifyContent:'flex-end' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}

// ── Change Username Card ──────────────────────────────────────────────────────
function ChangeUsernameCard() {
  const { user } = useAuth();
  const [newUsername, setNewUsername] = useState('');
  const [password, setPassword]       = useState('');
  const [saving, setSaving]           = useState(false);
  const { showToast } = useToast();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!newUsername.trim()) { showToast('Please enter a new username.', 'error'); return; }
    if (!password)           { showToast('Enter your current password to confirm.', 'error'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-username', { newUsername: newUsername.trim().toLowerCase(), currentPassword: password });
      showToast('Username changed successfully. Please log in again.', 'success');
      // Force re-login since token username changed
      setTimeout(() => {
        sessionStorage.removeItem('dq_token');
        sessionStorage.removeItem('dq_user');
        window.location.href = '/login';
      }, 1800);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not change username.', 'error');
    } finally { setSaving(false); }
  }

  return (
    <div className="card" style={{ padding:24 }}>
      <h2 style={{ fontSize:16, fontWeight:700, margin:'0 0 16px', display:'flex', alignItems:'center', gap:8 }}>
        Change Login Username
      </h2>
      <p style={{ fontSize:13, color:'var(--color-ink-faint)', margin:'0 0 16px' }}>
        Current username: <strong>@{user?.username}</strong>
      </p>
      <form onSubmit={handleSubmit}>
        <div className="field">
          <label className="label">New Username</label>
          <input className="input" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="e.g. dr.vijaya" autoComplete="off" />
        </div>
        <div className="field">
          <label className="label">Current Password (to confirm)</label>
          <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your current password" />
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving} style={{ display:'flex', alignItems:'center', gap:8 }}>
          {saving ? 'Saving…' : 'Change Username'}
        </button>
      </form>
    </div>
  );
}

// ── Change Password Card ──────────────────────────────────────────────────────
function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  async function handleSubmit(e) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast('New password and confirmation do not match.', 'error');
      return;
    }
    if (newPassword.length < 4) {
      showToast('New password must be at least 4 characters.', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      showToast('Password changed successfully.', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not change password.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card" style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <KeyRound size={20} color="var(--color-primary)" />
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Change Your Password</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label className="label">Current Password</label>
          <input className="input" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label className="label">New Password</label>
          <input className="input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label className="label">Confirm New Password</label>
          <input className="input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
        </div>
      </div>
      <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: 16 }}>
        <Save size={16} />
        {saving ? 'Saving...' : 'Update Password'}
      </button>
    </form>
  );
}

function StaffManagementCard() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStaff, setNewStaff] = useState({ username: '', password: '', displayName: '', role: 'doctor', isTemp: false });
  const [creating, setCreating] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function handleDeleteStaff() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/auth/staff/${deleteTarget._id}`);
      setStaff(prev => prev.filter(s => s._id !== deleteTarget._id));
      showToast(`Account @${deleteTarget.username} removed.`, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not remove account.', 'error');
    }
    setDeleteTarget(null);
  }
  const [toggling, setToggling] = useState(false);
  const { showToast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/staff');
      setStaff(res.data.staff);
    } catch (err) {
      showToast('Could not load staff accounts.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newStaff.username.trim() || !newStaff.password) {
      showToast('Username and password are required.', 'error');
      return;
    }
    if (newStaff.password.length < 4) {
      showToast('Password must be at least 4 characters.', 'error');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/auth/staff', newStaff);
      showToast(res.data.message, 'success');
      setStaff((prev) => [...prev, res.data.user]);
      setNewStaff({ username: '', password: '', displayName: '', role: 'doctor', isTemp: false });
      setShowAddForm(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not create the account.', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function confirmToggle() {
    if (!toggleTarget) return;
    setToggling(true);
    try {
      const res = await api.patch(`/auth/staff/${toggleTarget._id}/status`, { isActive: !toggleTarget.isActive });
      showToast(res.data.message, 'success');
      setStaff((prev) => prev.map((s) => (s._id === toggleTarget._id ? res.data.user : s)));
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update the account.', 'error');
    } finally {
      setToggling(false);
      setToggleTarget(null);
    }
  }

  return (
    <div className="card" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Users size={20} color="var(--color-primary)" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Clinic Staff Accounts</h2>
        </div>
        {/* Only the admin doctor can add new accounts */}
        {isAdmin && (
          <button className="btn btn-outline btn-sm" onClick={() => setShowAddForm((s) => !s)}>
            <UserPlus size={14} />
            {showAddForm ? 'Cancel' : 'Add Doctor or Receptionist'}
          </button>
        )}
      </div>

      {/* Admin-only: role badge indicating who has admin privileges */}
      {isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 12px', borderRadius: 8, background: 'var(--color-primary-soft)', marginBottom: 16, fontSize: 12, color: 'var(--color-primary)', fontWeight: 600 }}>
          <ShieldCheck size={14} />
          You are the clinic administrator. Only you can add new staff and change account access.
        </div>
      )}

      {isAdmin && showAddForm && (
        <form onSubmit={handleCreate} className="card" style={{ padding: 18, marginBottom: 18, background: 'var(--color-bg)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">Role</label>
              <select className="select" value={newStaff.role} onChange={(e) => setNewStaff((s) => ({ ...s, role: e.target.value }))}>
                <option value="doctor">Doctor</option>
                <option value="receptionist">Receptionist</option>
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">Display Name</label>
              <input className="input" value={newStaff.displayName} onChange={(e) => setNewStaff((s) => ({ ...s, displayName: e.target.value }))} placeholder="e.g. Dr. Mehta" />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">Username</label>
              <input className="input" value={newStaff.username} onChange={(e) => setNewStaff((s) => ({ ...s, username: e.target.value }))} placeholder="e.g. dr.mehta" required />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">Password</label>
              <input className="input" type="password" value={newStaff.password} onChange={(e) => setNewStaff((s) => ({ ...s, password: e.target.value }))} placeholder="At least 4 characters" required />
            </div>
          </div>
          {/* Trainee/temp flag */}
          <label style={{ display:'flex', alignItems:'center', gap:8, marginTop:12, fontSize:13, cursor:'pointer' }}>
            <input type="checkbox" checked={!!newStaff.isTemp}
              onChange={e => setNewStaff(s => ({ ...s, isTemp: e.target.checked }))}
              style={{ accentColor:'var(--color-warning)', width:15, height:15 }} />
            <span><strong>Trainee / Visiting Doctor</strong> — can be fully removed once their rotation ends (slots also removed)</span>
          </label>
          <button type="submit" className="btn btn-primary btn-sm" disabled={creating} style={{ marginTop: 14 }}>
            <UserPlus size={14} />
            {creating ? 'Creating...' : 'Create Account'}
          </button>
        </form>
      )}

      {loading ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {[1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 50, borderRadius: 8 }} />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {staff.map((s) => (
            <div
              key={s._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                opacity: s.isActive ? 1 : 0.55,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: s.role === 'doctor' ? 'var(--color-primary-soft)' : 'var(--color-accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {s.role === 'doctor' ? (
                    <Stethoscope size={15} color="var(--color-primary)" />
                  ) : (
                    <Headset size={15} color="var(--color-accent)" />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {s.displayName || s.username}
                    {s._id === user?.id && (
                      <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--color-ink-faint)' }}>(you)</span>
                    )}
                    {s.isAdmin && (
                      <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--color-primary-soft)', color: 'var(--color-primary)', borderRadius: 100, padding: '1px 7px' }}>ADMIN</span>
                    )}
                    {s.isTemp && (
                      <span style={{ fontSize: 10, fontWeight: 700, background: '#fff7ed', color: '#d97706', borderRadius: 100, padding: '1px 7px', border:'1px solid #fed7aa' }}>TRAINEE</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-ink-faint)' }}>
                    @{s.username} · {s.role}
                    {!s.isActive && ' · deactivated'}
                  </div>
                </div>
              </div>
              {/* Deactivate/Reactivate or Remove for temp accounts */}
              {isAdmin && String(s._id) !== String(user?.id) && (
                <div style={{ display:'flex', gap:6 }}>
                  {!s.isAdmin && (
                    <button
                      className="btn btn-danger btn-sm"
                      title={s.isTemp ? "Remove trainee account" : "Permanently remove this account"}
                      onClick={() => setDeleteTarget(s)}>
                      Remove
                    </button>
                  )}
                  <button
                    className="btn btn-icon btn-ghost"
                    title={s.isActive ? 'Deactivate account' : 'Reactivate account'}
                    onClick={() => setToggleTarget(s)}>
                    <Power size={16} color={s.isActive ? 'var(--color-danger)' : 'var(--color-success)'} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          marginTop: 16,
          fontSize: 12,
          color: 'var(--color-ink-faint)',
        }}
      >
        <ShieldCheck size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        {isAdmin
          ? 'As admin, you can add new accounts and deactivate/reactivate staff. Deactivating blocks login but keeps all history — you can reactivate at any time.'
          : 'Only the clinic administrator (main doctor) can add or deactivate staff accounts.'}
      </div>

      <ConfirmDialog
        open={!!toggleTarget}
        title={toggleTarget?.isActive ? `Deactivate ${toggleTarget?.displayName || toggleTarget?.username}?` : `Reactivate ${toggleTarget?.displayName || toggleTarget?.username}?`}
        message={
          toggleTarget?.isActive
            ? 'They will no longer be able to log in until reactivated. Their account and history are kept.'
            : 'They will be able to log in again immediately with their existing username and password.'
        }
        confirmLabel={toggling ? 'Saving...' : toggleTarget?.isActive ? 'Deactivate' : 'Reactivate'}
        danger={toggleTarget?.isActive}
        onConfirm={confirmToggle}
        onCancel={() => setToggleTarget(null)}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title={`Permanently remove ${deleteTarget?.displayName || deleteTarget?.username}?`}
        message={`This permanently removes @${deleteTarget?.username}'s account. Their appointment slots will be removed. Patient records they created are kept. This cannot be undone.`}
        confirmLabel="Yes, Remove"
        danger
        onConfirm={handleDeleteStaff}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
